import type { JSX } from 'react';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AUDIENCE_MIN_SAMPLE, type AudienceGeo } from '../api/types';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });
const languageNames = new Intl.DisplayNames(['en'], { type: 'language', fallback: 'code' });

function countryLabel(code: string): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/** 🇦🇪 from "AE". Windows renders the letters instead, which still reads fine. */
export function flag(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function pct(n: number, of: number): number {
  return of > 0 ? Math.round((n / of) * 100) : 0;
}

/**
 * Where a creator posts from, and where their audience is.
 *
 * Two different facts, shown differently on purpose. The flag is the
 * creator's country — cheap, and often not where the audience is (two of six
 * UAE-based meme pages measured had mostly Nigerian and Ethiopian
 * commenters). The chip is the audience, measured from commenters, and it's
 * what an "audience in X" search actually filtered on.
 *
 * Same rule as PresenterBadge: an audience that was never measured renders
 * NOTHING. It is not "audience elsewhere".
 */
export function AudienceBadge({
  region,
  audience,
  className,
}: {
  region?: string | null;
  audience?: AudienceGeo | null;
  className?: string;
}): JSX.Element | null {
  const code = String(region ?? '').toUpperCase();
  const hasRegion = /^[A-Z]{2}$/.test(code);
  const measured = audience && audience.sample > 0 ? audience : null;
  if (!hasRegion && !measured) return null;

  const top = measured
    ? Object.entries(measured.countries).sort((a, b) => b[1] - a[1])
    : [];
  const thin = measured ? measured.sample < AUDIENCE_MIN_SAMPLE : false;

  const method = measured?.method;
  const source =
    method === 'ig-stats'
      ? 'Audience, from Instagram audience data (a third-party estimate):'
      : method?.startsWith('x-repliers')
        ? `Audience, from ${measured!.sample} repliers${method.includes('followers') ? ' and followers' : ''} with a known location${method === 'x-repliers-verified' ? ' (confirmed with X)' : ''}${measured?.languageAssisted ? ' or a single-country language' : ''}:`
        : measured
          ? `Audience, from ${measured.sample} commenters on ${measured.videos} recent video${measured.videos === 1 ? '' : 's'}:`
          : '';
  const title = measured
    ? [
        source,
        ...top.slice(0, 6).map(([c, n]) => `  ${flag(c)} ${countryLabel(c)} ${pct(n, measured.sample)}%`),
        Object.keys(measured.languages).length
          ? `Languages: ${Object.entries(measured.languages)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([l, n]) => `${languageNames.of(l) ?? l} ${pct(n, measured.sample)}%`)
              .join(', ')}`
          : '',
        thin ? `\nOnly ${measured.sample} people measured — too few to trust.` : '',
        `Measured ${new Date(measured.checkedAt).toLocaleDateString()}.`,
      ]
        .filter(Boolean)
        .join('\n')
    : '';

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {hasRegion && (
        <span
          className="text-xs leading-none text-muted-foreground"
          title={`Posts from ${countryLabel(code)}. Where the creator is, not necessarily their audience.`}
        >
          {flag(code)} <span className="tabular-nums">{code}</span>
        </span>
      )}
      {measured && top[0] && (
        <span
          title={title}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium leading-none tabular-nums',
            thin
              ? 'border-border bg-muted/40 text-muted-foreground'
              : 'border-sky-600/35 bg-sky-600/10 text-sky-800 dark:text-sky-300',
          )}
        >
          <Users className="h-3 w-3 shrink-0" strokeWidth={2} />
          {flag(top[0][0])} {top[0][0]} {pct(top[0][1], measured.sample)}%
          {top[1] && (
            <span className="font-normal opacity-70">
              · {top[1][0]} {pct(top[1][1], measured.sample)}%
            </span>
          )}
          {thin && <span className="opacity-70">?</span>}
        </span>
      )}
    </span>
  );
}
